import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { inject, observer } from 'mobx-react';

import { reverse } from 'named-urls';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import { EntityModules, UserRole } from 'core/constants';
import routes from 'core/routes';

import UserRoute from 'components/Route/UserRoute';
import { CenteredCircularProgress } from 'components/Progress';
import Devices from 'containers/Devices';
import UserDetails from 'containers/UserDetails';
import DeviceDetails from 'containers/DeviceDetails';
import Users from 'containers/Users';
import AlarmsConfigure from 'containers/AlarmsConfigure';

import AlarmDetails from 'containers/AlarmDetails';
import Manage from 'containers/Manage';

import { getFirstAvailableLoneWorkingReportingSlug } from 'containers/LoneWorkingReporting/constants';
import UsageReport from 'containers/Reports/components/UsageReport';

import TaskDetails from 'containers/TaskDetails';

import Tasks from 'containers/Tasks';

import KnockNotifications from 'containers/KnockNotifications';

import DisableFeatures from 'containers/DisableFeatures/DisableFeatures';

import { AmberAlarms, RedAlarms } from 'containers/Alarms/Alarms';

import Teams from 'containers/Admin/Teams';

import Home from 'containers/Home';

import Licenses from 'containers/Licenses';

import TeamDetails from 'containers/Admin/TeamDetails';

import ProfileSecuritySettings from 'containers/ProfileSecuritySettings';
import NotificationSettings from 'containers/NotificationSettings';
import Preferences from 'containers/Preferences';

import Forms from 'containers/Forms';

import FormTemplates from 'containers/FormTemplates';

import Audits from 'containers/Audits';

import AuditTemplates from 'containers/AuditTemplates';
import RiskAssessmentTemplates from 'containers/RiskAssessmentTemplates';

import CompanySettings from 'containers/CompanySettings';

import MoreApps from 'containers/MoreApps';

import Sites from 'containers/Sites';
import LayoutEditor from 'containers/LayoutEditor';

import SiteDetails from 'containers/SiteDetails';
import FieldManager from 'containers/FieldManager';
import UserFieldsSettings from 'containers/UserFieldsSettings';
import OrganisationStructure from 'containers/OrganisationStructure';
import MemberGroups from 'containers/MemberGroups';
import MemberGroupCreate from 'containers/MemberGroups/MemberGroupCreate';
import MemberGroupDetails from 'containers/MemberGroups/MemberGroupDetails';

import Assets from 'containers/Assets';

import AssetsDetails from 'containers/AssetsDetails/AssetsDetails';

import RelatedActions from 'components/Entities/EntityRelatedPage/RelatedActions';

import RelatedAudits from 'components/Entities/EntityRelatedPage/RelatedAudits';

import Contacts from 'containers/Contacts';

import ContactsDetails from 'containers/ContactsDetails';
import Risks from 'containers/Risks';
import RisksIndex from 'containers/Risks/RisksIndex';
import RisksDetails from 'containers/RisksDetails';
import Controls from 'containers/Controls';
import Hazards from 'containers/Hazards';
import HazardDetails from 'containers/HazardDetails';
import ControlDetails from 'containers/ControlDetails';

import RelatedEntities from 'components/Entities/EntityRelatedPage/RelatedEntities';

import EventForms from 'containers/EventForms';

import Events from 'containers/Events';

import EntitiesFormSubmission from 'containers/EntitiesFormSubmission';

import RiskAssessments from 'containers/RiskAssessments';

import Documents from 'containers/Documents';

import AcknowledgementRequests from 'containers/AcknowledgementRequests';
import AcknowledgementRequestNew from 'containers/AcknowledgementRequests/AcknowledgementRequestNew/AcknowledgementRequestNew';
import AcknowledgementRequestDetails from 'containers/AcknowledgementRequestDetails';
import AcknowledgementPage from 'containers/AcknowledgementPage';
import AssignmentRules from 'containers/AssignmentRules';

import Pipelines from 'containers/Pipelines';

import PipelinesDetails from 'containers/PipelinesDetails';

import Schedules from 'containers/Schedules';

import ScheduleDetails from 'containers/ScheduleDetails';

import RelatedActionsToAudits from 'containers/FormsDetails/components/RelatedTab/RelatedActionsToAudits';

import { canAccessRisksProduct, canAuthorRiskAssessmentTemplates } from 'utils/roles/permissions';
import { RISK_ASSESSMENT_BUILDER_MODE } from 'stores/AuditFormBuilder/mode';
import { ADMIN_AND_ACCOUNT_MANAGERS, ADMIN_AND_MANAGERS, NON_ARC_OPERATORS, NON_USER } from 'utils/roles/roleGroups';

import { InjectedRoutingProps } from './types';
import { RoutingContainer } from './styles';
import ActionsAllRedirect from './ActionsAllRedirect';
import ScheduleInstanceRedirect from './ScheduleInstanceRedirect';
import LegacyPrefixRedirect from './LegacyPrefixRedirect';

const ActionsRoute = ({ children }: { children: React.ReactElement }): React.ReactElement => (
  <UserRoute isAuthorizedUser={(user) => NON_ARC_OPERATORS.includes(user.role)}>{children}</UserRoute>
);

// Lazy-loaded: TipTap-heavy editors and analytics pages to reduce initial bundle parse time
const ActivityMap = React.lazy(() => import('containers/ActivityMap'));
const Analytics = React.lazy(() => import('containers/Analytics'));
const MainAnalytics = React.lazy(() => import('containers/Analytics/components/MainAnalytics'));
const LoneWorkingReportingDashboard = React.lazy(() => import('containers/LoneWorkingReporting'));
const SafetyMetrics = React.lazy(() => import('containers/SafetyMetrics'));
const FormsDetails = React.lazy(() => import('containers/FormsDetails'));
const TemplateDetails = React.lazy(() => import('containers/TemplateDetails'));
const AuditRecord = React.lazy(() => import('containers/AuditRecord'));
const AuditFormBuilder = React.lazy(() => import('containers/AuditFormBuilder'));
const EntitiesFormBuilder = React.lazy(() => import('containers/EntitiesFormBuilder'));
const EventDetails = React.lazy(() => import('containers/EventDetails'));
const RiskAssessmentsDetails = React.lazy(() => import('containers/RiskAssessmentsDetails'));
const DocumentsDetails = React.lazy(() => import('containers/DocumentsDetails'));

const Routing = ({ session, isDetailsView = false }: InjectedRoutingProps): React.ReactElement => {
  // Early return if session is not available
  if (!session) {
    return <CenteredCircularProgress />;
  }

  return (
    <RoutingContainer $isDetailsView={isDetailsView} id="routing-container">
      <React.Suspense fallback={<CenteredCircularProgress />}>
        <Routes>
          {/* Home */}
          <Route path={String(routes.dashboard.home)} element={<Home />} />

          {/* Recipient acknowledgement page — gated on the Documents module licence only; any role
            can be a recipient. The backend still enforces ownership + the org flag (404 → the page
            renders the generic error page). */}
          <Route
            path={routes.dashboard.acknowledgements.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <AcknowledgementPage />
              </UserRoute>
            }
          />

          {/* Inbox (merged: legacy notifications + Knock feed) */}
          <Route
            path={String(routes.dashboard.notifications)}
            element={
              <UserRoute isAuthorizedUser={() => session.user.hasFunctionalModuleLicense()}>
                <KnockNotifications />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.notifications.category)}
            element={
              <UserRoute isAuthorizedUser={() => session.user.hasFunctionalModuleLicense()}>
                <KnockNotifications />
              </UserRoute>
            }
          />

          {/* Actions — per-surface routes */}
          <Route
            path={String(routes.dashboard.tasks.list)}
            element={
              <ActionsRoute>
                <Tasks surface="tasks" />
              </ActionsRoute>
            }
          />
          <Route
            path={String(routes.dashboard.tasks.my)}
            element={<Navigate replace to={routes.dashboard.tasks.list.toString()} />}
          />
          <Route
            path={String(routes.dashboard.tasks.audits)}
            element={
              <ActionsRoute>
                <Tasks surface="audits" />
              </ActionsRoute>
            }
          />
          <Route
            path={String(routes.dashboard.tasks.acknowledgements)}
            element={
              <ActionsRoute>
                <Tasks surface="acknowledgements" />
              </ActionsRoute>
            }
          />
          <Route
            path={String(routes.dashboard.tasks.risks)}
            element={
              <ActionsRoute>
                <Tasks surface="risks" />
              </ActionsRoute>
            }
          />
          <Route
            path={String(routes.dashboard.tasks.riskAction)}
            element={
              <ActionsRoute>
                <Tasks surface="risks" />
              </ActionsRoute>
            }
          />
          <Route
            path={String(routes.dashboard.tasks.scheduleInstance)}
            element={
              <ActionsRoute>
                <Tasks surface="audits" />
              </ActionsRoute>
            }
          />
          <Route
            path={routes.dashboard.tasks.details}
            element={
              <UserRoute
                isAuthorizedUser={(user) => NON_ARC_OPERATORS.includes(user.role) && user.hasFunctionalModuleLicense()}
              >
                <TaskDetails />
              </UserRoute>
            }
          />

          {/* Legacy Actions redirects — old tabbed routes resolve to their per-surface equivalents */}
          <Route path={String(routes.dashboard.tasks.all)} element={<ActionsAllRedirect />} />
          <Route
            path="/actions/all/schedule/:scheduleId/instance/:instanceId/"
            element={<ScheduleInstanceRedirect />}
          />

          {/* Schedules */}
          <Route
            path={String(routes.dashboard.schedules)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.scheduleEnabled && ADMIN_AND_MANAGERS.includes(user.role)}>
                <Schedules />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.schedules.new)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.scheduleEnabled && ADMIN_AND_MANAGERS.includes(user.role)}>
                <ScheduleDetails />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.schedules.details)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.scheduleEnabled && ADMIN_AND_MANAGERS.includes(user.role)}>
                <ScheduleDetails />
              </UserRoute>
            }
          />

          {/* Lone working */}
          <Route
            path={`${String(routes.dashboard.alarms.escalationProfiles)}/*`}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_MANAGERS.includes(user.role)}>
                <AlarmsConfigure />
              </UserRoute>
            }
          />
          <Route
            path={routes.dashboard.alarms.activityMap}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.licenses.protectorAlarms &&
                  (user.organisation.activityMap || [UserRole.ArcOperator, UserRole.Admin].includes(user.role))
                }
                onUnauthorizedRedirectProducts
              >
                <ActivityMap />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms)}
            element={
              <UserRoute isAuthorizedUser={(user) => NON_USER.includes(user.role)}>
                <Navigate to={routes.dashboard.alarms.red} replace />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.red)}
            element={
              <UserRoute isAuthorizedUser={(user) => NON_USER.includes(user.role)}>
                <RedAlarms />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.amber)}
            element={
              <UserRoute isAuthorizedUser={(user) => NON_USER.includes(user.role)}>
                <AmberAlarms />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.manage)}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_MANAGERS.includes(user.role)}>
                <Manage />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.devices)}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_MANAGERS.includes(user.role)}>
                <Devices />
              </UserRoute>
            }
          />
          <Route
            path={routes.dashboard.alarms.details}
            element={
              <UserRoute isAuthorizedUser={(user) => NON_USER.includes(user.role)}>
                <AlarmDetails />
              </UserRoute>
            }
          />
          <Route
            path={routes.dashboard.alarms.devices.details}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_MANAGERS.includes(user.role)}>
                <DeviceDetails />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.reports)}
            element={
              <UserRoute isAuthorizedUser={(user) => !user.loneWorkingAnalyticsEnabled}>
                <UsageReport />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.reporting)}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.loneWorkingAnalyticsEnabled && ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)
                }
              >
                <Navigate
                  to={reverse(routes.dashboard.alarms.reporting.details, {
                    reportingType: getFirstAvailableLoneWorkingReportingSlug(
                      session.user?.dashboards?.loneWorkingReporting
                    ),
                  })}
                  replace
                />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.alarms.reporting.details)}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.loneWorkingAnalyticsEnabled && ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)
                }
              >
                <LoneWorkingReportingDashboard />
              </UserRoute>
            }
          />

          {/* Audits */}
          <Route
            path={String(routes.dashboard.audits)}
            element={
              <UserRoute isAuthorizedUser={(user) => NON_USER.includes(user.role)}>
                <Navigate to={routes.dashboard.audits.forms.toString()} replace />
              </UserRoute>
            }
          />
          {/* Audits list routes dual-route per the audits-as-entity flag: entity containers when the
            Audits module is active, legacy workflows-api containers otherwise. A single shared path
            per URL with the element branched by the flag (React Router v6 picks the first matching
            Route, so two Routes cannot share a path). The legacy predicate also negates the module
            so a flag-on org — which still holds the `workflows` licence until decommission — never
            double-matches. */}
          <Route
            path={String(routes.dashboard.audits.forms)}
            element={
              session.user?.activeEntityModules.includes(EntityModules.Audits) ? (
                <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Audits)}>
                  <Audits />
                </UserRoute>
              ) : (
                <UserRoute
                  isAuthorizedUser={(user) =>
                    user.licenses.workflows && !user.activeEntityModules.includes(EntityModules.Audits)
                  }
                >
                  <Forms />
                </UserRoute>
              )
            }
          />
          <Route
            path={String(routes.dashboard.audits.forms.details)}
            element={
              session.user?.activeEntityModules.includes(EntityModules.Audits) ? (
                <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Audits)}>
                  <AuditRecord />
                </UserRoute>
              ) : (
                <UserRoute
                  isAuthorizedUser={(user) =>
                    user.licenses.workflows && !user.activeEntityModules.includes(EntityModules.Audits)
                  }
                >
                  <FormsDetails />
                </UserRoute>
              )
            }
          />
          {/* Audit fill-out. Entity-only: the legacy flow lives at the dual-routed record URL above. */}
          <Route
            path={String(routes.dashboard.audits.draft.details)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Audits)}>
                <EntitiesFormSubmission />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.audits.templates)}
            element={
              session.user?.activeEntityModules.includes(EntityModules.Audits) ? (
                <UserRoute
                  isAuthorizedUser={(user) =>
                    user.activeEntityModules.includes(EntityModules.Audits) &&
                    ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)
                  }
                >
                  <AuditTemplates />
                </UserRoute>
              ) : (
                <UserRoute
                  isAuthorizedUser={(user) =>
                    user.licenses.workflows &&
                    !user.activeEntityModules.includes(EntityModules.Audits) &&
                    ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)
                  }
                >
                  <FormTemplates />
                </UserRoute>
              )
            }
          />
          <Route
            path={String(routes.dashboard.audits.templates.details)}
            element={
              session.user?.activeEntityModules.includes(EntityModules.Audits) ? (
                // Same roles as the list; legacy's `role !== User` let a Manager in by URL.
                <UserRoute
                  isAuthorizedUser={(user) =>
                    user.activeEntityModules.includes(EntityModules.Audits) &&
                    ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)
                  }
                >
                  <AuditFormBuilder />
                </UserRoute>
              ) : (
                <UserRoute
                  isAuthorizedUser={(user) =>
                    user.licenses.workflows &&
                    !user.activeEntityModules.includes(EntityModules.Audits) &&
                    user.role !== UserRole.User
                  }
                >
                  <TemplateDetails />
                </UserRoute>
              )
            }
          />

          {/* Admin */}
          <Route
            path={String(routes.dashboard.settings.companySettings)}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)}>
                <CompanySettings />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.settings.users)}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)}>
                <Users />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.settings.users.details}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  [
                    UserRole.Admin,
                    UserRole.Manager,
                    UserRole.User,
                    UserRole.ArcOperator,
                    UserRole.AccountManager,
                  ].includes(user.role)
                }
              >
                <UserDetails />
              </UserRoute>
            }
          />
          <Route
            path={routes.dashboard.settings.licenses}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)}>
                <Licenses />
              </UserRoute>
            }
          />
          <Route
            path={String(routes.dashboard.settings.userFields)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.showObjectManager}>
                <UserFieldsSettings />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.settings.organisationStructure)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.role === UserRole.AccountManager && !!user.scopedPermissions}>
                <OrganisationStructure />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.settings.groups)}
            element={
              <UserRoute
                isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role) && !!user.groupsEnabled}
              >
                <MemberGroups />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.settings.groups.new)}
            element={
              <UserRoute
                isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role) && !!user.groupsEnabled}
              >
                <MemberGroupCreate />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.settings.groups.details)}
            element={
              <UserRoute
                isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role) && !!user.groupsEnabled}
              >
                <MemberGroupDetails />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.settings.teams)}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)}>
                <Teams />
              </UserRoute>
            }
          />

          <Route path={String(routes.dashboard.settings.teams.new)} element={<TeamDetails />} />

          <Route path={String(routes.dashboard.settings.teams.details)} element={<TeamDetails />} />

          {/* ============ */}
          <Route
            path={routes.dashboard.disableFeature.details.toString()}
            element={
              <UserRoute isAuthorizedUser={(user) => NON_ARC_OPERATORS.includes(user.role)}>
                <DisableFeatures />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.analytics)}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_MANAGERS.includes(user.role)}>
                <MainAnalytics />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.analytics.details}
            element={
              <UserRoute isAuthorizedUser={(user) => ADMIN_AND_MANAGERS.includes(user.role)}>
                <Analytics />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.settings.notifications.toString()}
            element={
              <UserRoute isAuthorizedUser={(user) => user.hasFunctionalModuleLicense()}>
                <NotificationSettings />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.settings.security.toString()}
            element={
              <UserRoute isAuthorizedUser={(user) => user !== undefined && user !== null}>
                <ProfileSecuritySettings />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.settings.preferences.toString()}
            element={
              <UserRoute isAuthorizedUser={(user) => user.timezoneModelEnabled}>
                <Preferences />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.moreApps)}
            element={
              <UserRoute
                isAuthorizedUser={(user) => user.role !== UserRole.ArcOperator && !user.hasAllProductLicenses()}
              >
                <MoreApps />
              </UserRoute>
            }
          />

          {/* Sites */}
          <Route
            path={String(routes.dashboard.sites)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <Sites />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <SiteDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.layoutEditor, { moduleName: EntityModules.Sites })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Sites) && user.showObjectManager
                }
              >
                <LayoutEditor />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.fieldManager, { moduleName: EntityModules.Sites })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Sites) && user.showObjectManager
                }
              >
                <FieldManager />
              </UserRoute>
            }
          />

          {/* related actions to entities */}
          <Route
            path={routes.dashboard.audits.relatedTasks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.licenses.workflows}>
                <RelatedActionsToAudits />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.audits.relatedActions}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) && user.licenses.workflows
                }
              >
                <RelatedActions />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedActions}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedActions />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedActions}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedActions />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedActions}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedActions />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedActions}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedActions />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedActions}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedActions />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedActions}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedActions />
              </UserRoute>
            }
          />

          {/* related audits to entities */}
          <Route
            path={routes.dashboard.sites.relatedAudits}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Sites) && user.licenses.workflows
                }
              >
                <RelatedAudits />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedAudits}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Assets) && user.licenses.workflows
                }
              >
                <RelatedAudits />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedAudits}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Contacts) && user.licenses.workflows
                }
              >
                <RelatedAudits />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedAudits}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.licenses.workflows
                }
              >
                <RelatedAudits />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedAudits}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Documents) && user.licenses.workflows
                }
              >
                <RelatedAudits />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedAudits}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Risks) && user.licenses.workflows
                }
              >
                <RelatedAudits />
              </UserRoute>
            }
          />

          {/* related sites to entities */}
          <Route
            path={routes.dashboard.audits.relatedSites}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.Sites) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedSites}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedSites}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedSites}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedSites}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedSites}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedSites}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* related assets to entities */}
          <Route
            path={routes.dashboard.audits.relatedAssets}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.Assets) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedAssets}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedAssets}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedAssets}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedAssets}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedAssets}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedAssets}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* related events to entities */}
          <Route
            path={routes.dashboard.audits.relatedEvents}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.Events) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedEvents}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedEvents}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedEvents}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedEvents}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedEvents}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedEvents}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* related contacts to entities */}
          <Route
            path={routes.dashboard.audits.relatedContacts}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.Contacts) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedContacts}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedContacts}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedContacts}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedContacts}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedContacts}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedContacts}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* related risk assessments to entities */}
          <Route
            path={routes.dashboard.audits.relatedRiskAssessments}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.RisksAssessments) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedRiskAssessments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedRiskAssessments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedRiskAssessments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedRiskAssessments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedRiskAssessments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedRiskAssessments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* related documents to entities */}
          <Route
            path={routes.dashboard.audits.relatedDocuments}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.Documents) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedDocuments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedDocuments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedDocuments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedDocuments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.riskAssessments.relatedDocuments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.RisksAssessments)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedDocuments}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* related risks to entities */}
          <Route
            path={routes.dashboard.audits.relatedRisks}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Audits) &&
                  user.activeEntityModules.includes(EntityModules.Risks) &&
                  user.licenses.workflows
                }
              >
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.sites.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Sites)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.riskAssessments.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.RisksAssessments)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.documents.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.relatedRisks}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RelatedEntities />
              </UserRoute>
            }
          />

          {/* Assets */}
          <Route
            path={String(routes.dashboard.assets)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <Assets />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.assets.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Assets)}>
                <AssetsDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.layoutEditor, { moduleName: EntityModules.Assets })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Assets) && user.showObjectManager
                }
              >
                <LayoutEditor />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.fieldManager, { moduleName: EntityModules.Assets })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Assets) && user.showObjectManager
                }
              >
                <FieldManager />
              </UserRoute>
            }
          />

          {/* contacts */}
          <Route
            path={String(routes.dashboard.contacts)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <Contacts />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.contacts.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Contacts)}>
                <ContactsDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.layoutEditor, {
              moduleName: EntityModules.Contacts,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Contacts) && user.showObjectManager
                }
              >
                <LayoutEditor />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.fieldManager, {
              moduleName: EntityModules.Contacts,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Contacts) && user.showObjectManager
                }
              >
                <FieldManager />
              </UserRoute>
            }
          />

          {/* risk register */}
          <Route
            path={String(routes.dashboard.risks)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RisksIndex />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.riskRegister}
            element={
              <UserRoute isAuthorizedUser={canAccessRisksProduct}>
                <Risks />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.risks.templates)}
            element={
              <UserRoute isAuthorizedUser={canAuthorRiskAssessmentTemplates}>
                <RiskAssessmentTemplates />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.controls}
            element={
              <UserRoute isAuthorizedUser={canAccessRisksProduct}>
                <Controls />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.templates.details}
            element={
              <UserRoute isAuthorizedUser={canAuthorRiskAssessmentTemplates}>
                <AuditFormBuilder mode={RISK_ASSESSMENT_BUILDER_MODE} />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.controlDetails}
            element={
              <UserRoute isAuthorizedUser={canAccessRisksProduct}>
                <ControlDetails />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.hazards}
            element={
              <UserRoute isAuthorizedUser={canAccessRisksProduct}>
                <Hazards />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.hazardDetails}
            element={
              <UserRoute isAuthorizedUser={canAccessRisksProduct}>
                <HazardDetails />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.risks.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Risks)}>
                <RisksDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.layoutEditor, {
              moduleName: EntityModules.Risks,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Risks) && user.showObjectManager
                }
              >
                <LayoutEditor />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.fieldManager, {
              moduleName: EntityModules.Risks,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Risks) && user.showObjectManager
                }
              >
                <FieldManager />
              </UserRoute>
            }
          />

          {/* risk assessments */}
          <Route
            path={String(routes.dashboard.riskAssessments)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.RisksAssessments)}>
                <RiskAssessments />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.riskAssessments.details)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.RisksAssessments)}>
                <RiskAssessmentsDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.layoutEditor, {
              moduleName: EntityModules.RisksAssessments,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.RisksAssessments) && user.showObjectManager
                }
              >
                <LayoutEditor />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.fieldManager, {
              moduleName: EntityModules.RisksAssessments,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.RisksAssessments) && user.showObjectManager
                }
              >
                <FieldManager />
              </UserRoute>
            }
          />

          {/* events */}
          <Route
            path={String(routes.dashboard.events)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <Events />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <EventDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.layoutEditor, { moduleName: EntityModules.Events })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.showObjectManager
                }
              >
                <LayoutEditor />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.fieldManager, { moduleName: EntityModules.Events })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.showObjectManager
                }
              >
                <FieldManager />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.settings.objects.details.formBuilder.details}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.showObjectManager
                }
              >
                <EntitiesFormBuilder />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), {
              moduleName: EntityModules.Events,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.showObjectManager
                }
              >
                <EventForms />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.pipelines.toString(), {
              moduleName: EntityModules.Events,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.showObjectManager
                }
              >
                <Pipelines />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.pipelines.details)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <PipelinesDetails />
              </UserRoute>
            }
          />

          {/* documents */}
          <Route
            path={String(routes.dashboard.documents)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <Documents />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.documents.acknowledgementRequests)}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Documents) &&
                  user.documentAcknowledgementsEnabled &&
                  ADMIN_AND_MANAGERS.includes(user.role)
                }
              >
                <AcknowledgementRequests />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.documents.acknowledgementRequests.new)}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Documents) &&
                  user.documentAcknowledgementsEnabled &&
                  ADMIN_AND_MANAGERS.includes(user.role)
                }
              >
                <AcknowledgementRequestNew />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.documents.acknowledgementRequests.details)}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Documents) &&
                  user.documentAcknowledgementsEnabled &&
                  ADMIN_AND_MANAGERS.includes(user.role)
                }
              >
                <AcknowledgementRequestDetails />
              </UserRoute>
            }
          />

          <Route
            path={String(routes.dashboard.documents.details)}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Documents)}>
                <DocumentsDetails />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.safetyMetrics.toString(), {
              moduleName: EntityModules.Events,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) &&
                  user.showObjectManager &&
                  ADMIN_AND_ACCOUNT_MANAGERS.includes(user.role)
                }
              >
                <SafetyMetrics />
              </UserRoute>
            }
          />

          <Route
            path={reverse(routes.dashboard.settings.objects.details.assignmentRules.toString(), {
              moduleName: EntityModules.Events,
            })}
            element={
              <UserRoute
                isAuthorizedUser={(user) =>
                  user.activeEntityModules.includes(EntityModules.Events) && user.showObjectManager
                }
              >
                <AssignmentRules />
              </UserRoute>
            }
          />

          <Route
            path={routes.dashboard.events.draft.details}
            element={
              <UserRoute isAuthorizedUser={(user) => user.activeEntityModules.includes(EntityModules.Events)}>
                <EntitiesFormSubmission />
              </UserRoute>
            }
          />

          <Route path="/admin/*" element={<LegacyPrefixRedirect to="/settings" emptyTo="/settings/users/" />} />
          <Route path="/object-manager/*" element={<LegacyPrefixRedirect to="/settings/objects" />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
    </RoutingContainer>
  );
};

export default inject('session')(observer(Routing));
